import React, { useMemo } from 'react';

import { useCurrentUser } from 'Auth';

import { useChildComponent } from '@gogame/hooks-ts';
import { DateValueFormatter } from '@gogame/intl-ts';
import Permissions from '@gogame/permissions';

import { DummyCampaignSidePage } from 'scenes/Campaigns/GGCampaigns/components/CampaignForm/CampaignCreatedSidePage/DummyCampaignSidePage';
import {
  CostModelFormCreate,
  CostModelFormEdit,
} from 'scenes/Campaigns/GGCampaigns/components/CostModelForm';
import { useLoadCampaign } from 'scenes/Campaigns/GGCampaigns/data';
import {
  useCreateCampaign,
  useEditCampaign,
  useCreateDummyCampaign,
} from 'scenes/Campaigns/GGCampaigns/data/mutations';

import {
  GENERATE_NAME_AUTOMATICALLY,
  CAMPAIGN_NAME,
} from 'scenes/Campaigns/components/Form/Campaign/constants';
import DummyCampaignForm from './DummyCampaignForm';
import MultiCampaignsForm from './MultiCampaignsForm';
import SimpleCampaignForm from './SimpleCampaignForm';
import { editGranularityFields, createGranularityFields } from './granularity';
import {
  createMultiCampaigns,
  duplicateCampaign,
  duplicateMultiCampaigns,
  createDummyCampaign,
} from './schema';
import { withCreateChaining } from './withCreateChaining';

export const CampaignFormCreate = ({
  onSubmit,
  onClose,
  initialValues: initialValuesProp,
  onExited,
  open,
}) => {
  const [createCampaign, { isLoading }] = useCreateCampaign();

  const handleSubmit = ({ missingMediaSource, ...values }) => {
    createCampaign(values).then(onSubmit);
  };

  const user = useCurrentUser();
  const initialValues = useMemo(() => {
    return initialValuesProp || createMultiCampaigns(user.id);
  }, [initialValuesProp, user.id]);
  return (
    <MultiCampaignsForm
      title={'Add Campaign'}
      onSubmit={handleSubmit}
      onClose={onClose}
      initialValues={initialValues}
      CostModelFormCreate={CostModelFormCreate}
      CostModelFormEdit={CostModelFormCreate}
      removableCostModels={true}
      loading={isLoading}
      onExited={onExited}
      open={open}
    />
  );
};

const normalizeSubmitValues = ({
  [CAMPAIGN_NAME]: name,
  [GENERATE_NAME_AUTOMATICALLY]: generateNameAutomatically,
  ...values
}) => ({
  ...values,
  [CAMPAIGN_NAME]: generateNameAutomatically ? null : name,
});

export const DummyCampaignFormCreate = ({
  onClose,
  initialValues: initialValuesProp,
  onExited,
  open,
  ...props
}) => {
  const [createCampaign, { isLoading }] = useCreateDummyCampaign();
  const [childComponent, showChildComponent] = useChildComponent();

  const handleSubmit = (values) => {
    const { period, ...rest } = values;

    const data = {
      ...normalizeSubmitValues(rest),
      period: {
        from: period[0],
        to: period[1],
      },
    };

    createCampaign(data).then((campaign) => {
      showChildComponent(DummyCampaignSidePage, {
        campaign,
        ...(props.onSubmit && {
          onSubmit: props.onSubmit,
        }),
      });
    });
  };

  const user = useCurrentUser();
  const initialValues = useMemo(() => {
    return createDummyCampaign({
      ...initialValuesProp,
      ownerId: user.id,
    });
  }, [initialValuesProp, user.id]);

  return (
    <DummyCampaignForm
      title={'Add Campaign'}
      onSubmit={handleSubmit}
      onClose={onClose}
      initialValues={initialValues}
      loading={isLoading}
      onExited={onExited}
      open={open}
    >
      {childComponent}
    </DummyCampaignForm>
  );
};

export const MultiCampaignsFormDuplicate = ({ initialValues, ...props }) => {
  //we should use it for create confirm Dialog to duplicate two campaigns at once

  return (
    <CampaignFormCreate
      {...props}
      initialValues={duplicateMultiCampaigns(initialValues)}
    />
  );
};

export const CampaignFormEdit = ({
  onClose,
  id,
  name,
  onExited,
  open,
  onChange,
  onSubmit,
}) => {
  const [editCampaign, { isLoading: loading }] = useEditCampaign();
  const { data, isLoading } = useLoadCampaign({ id });
  const {
    partnerCampaignIdCreatedDate,
    partnerCampaignIdCreator,
    editGranularity = '',
    ...initialValues
  } = data || {};

  const handleSubmit = (values) =>
    editCampaign({ id, ...values }).then(onSubmit);

  const creator = useMemo(() => {
    if (!partnerCampaignIdCreatedDate) {
      return null;
    }

    return (
      <>
        Added by {partnerCampaignIdCreator?.name},{' '}
        <DateValueFormatter
          template={'LL'}
          value={partnerCampaignIdCreatedDate}
        />
      </>
    );
  }, [partnerCampaignIdCreatedDate]);

  return (
    <Permissions actions={editGranularityFields(editGranularity)}>
      <SimpleCampaignForm
        id={id}
        title={'Edit GG Campaign'}
        name={name}
        loading={isLoading || loading}
        active={!!data}
        onSubmit={handleSubmit}
        onClose={onClose}
        initialValues={initialValues}
        creator={creator}
        CostModelFormCreate={CostModelFormCreate}
        CostModelFormEdit={CostModelFormEdit}
        onExited={onExited}
        open={open}
        onChange={onChange}
      />
    </Permissions>
  );
};

export const CampaignFormDuplicate = ({
  onSubmit,
  onClose,
  id,
  onExited,
  open,
}) => {
  const [createCampaign, { isLoading: loading }] = useCreateCampaign();
  const { data, isLoading } = useLoadCampaign({ id });

  const {
    /* eslint-disable */
    partnerCampaignIdCreatedDate,
    partnerCampaignIdCreator,
    promocodeId,
    /* eslint-enable */
    ...campaign
  } = data || {};

  const handleSubmit = (values) => createCampaign({ ...values }).then(onSubmit);

  return (
    <Permissions actions={createGranularityFields()}>
      <MultiCampaignsForm
        title={'Add Campaign'}
        loading={isLoading || loading}
        active={!!data}
        onSubmit={handleSubmit}
        onClose={onClose}
        initialValues={data && duplicateCampaign(campaign)}
        CostModelFormCreate={CostModelFormCreate}
        CostModelFormEdit={CostModelFormCreate}
        removableCostModels={true}
        onExited={onExited}
        open={open}
      />
    </Permissions>
  );
};

export const withCampaignCreateChaining = withCreateChaining({
  CreateForm: CampaignFormCreate,
  DuplicateForm: MultiCampaignsFormDuplicate,
});

export const CampaignFormCreateWithChaining =
  withCampaignCreateChaining(CampaignFormCreate);

export const CampaignFormDuplicateWithChaining = withCampaignCreateChaining(
  CampaignFormDuplicate
);
